import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ThemeMode = "light" | "dark" | "orange";

/**
 * A cor da barra do sistema no app instalado.
 *
 * Precisa ser escrita em runtime porque o tema aqui é uma classe no <html>, e
 * não `prefers-color-scheme`: um valor fixo no HTML deixaria a barra laranja
 * com o app no tema escuro. Espelha `--color-header-bg` de cada tema.
 */
const THEME_COLOR: Record<ThemeMode, string> = {
  orange: "#ff6600",
  light: "#ffffff",
  dark: "#1f2937",
};

function applyThemeColor(theme: ThemeMode): void {
  if (typeof document === "undefined") return;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[theme]);
}

export interface ThemeState {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: "orange", // Default theme

      setTheme: (theme) => {
        // Update the document class for theme switching
        const root = document.documentElement;
        root.classList.remove("theme-light", "theme-dark", "theme-orange");
        root.classList.add(`theme-${theme}`);
        applyThemeColor(theme);
        set({ theme });
      },
    }),
    {
      name: "t2c-theme",
      onRehydrateStorage: () => (state) => {
        // Apply theme on rehydration
        if (state) {
          const root = document.documentElement;
          root.classList.remove("theme-light", "theme-dark", "theme-orange");
          root.classList.add(`theme-${state.theme}`);
          applyThemeColor(state.theme);
        }
      },
    },
  ),
);

// Initialize theme on app load
export function initializeTheme() {
  const state = useThemeStore.getState();
  const root = document.documentElement;
  root.classList.remove("theme-light", "theme-dark", "theme-orange");
  root.classList.add(`theme-${state.theme}`);
  applyThemeColor(state.theme);
}
